import React, { useState, useCallback, useEffect, useMemo } from 'react'
import {
    ReactFlow,
    MiniMap,
    Controls,
    Background,
    ReactFlowProvider,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { Button, Alert } from 'reactstrap'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'

import {
    AllAvailableModelsQuery,
    ActionTypesAndParamsQuery,
    ConditionTypes,
    TriggerTypes,
    AutomationQuery,
    AvailableModelQuery,
} from '../Queries'
import {
    CREATE_AUTOMATION_WORKFLOW,
    UpdateAutomationMutation,
    UpdateTriggerMutation,
    CreateActionMutation,
    UpdateActionMutation,
    DeleteActionMutation,
    CreateConditionGroupMutation,
    UpdateConditionGroupMutation,
    CreateConditionMutation,
    UpdateConditionMutation,
    DeleteConditionMutation,
    DeleteConditionGroupMutation,
} from '../Mutations'

import useWorkflowState from './useWorkflowState'
import ConfigPanel from './panels/ConfigPanel'
import ExecutionLogsList from './ExecutionLogsList'
import toTitleCase from '../../Functions/toTitleCase'
import StartNode from './nodes/StartNode'
import ConditionGroupNode from './nodes/ConditionGroupNode'
import ActionNode from './nodes/ActionNode'
import AddButton from './nodes/AddButton'
import { ROUTES } from '../../Layout/routes'

import './WorkflowBuilder.css'

const nodeTypes = {
    start: StartNode,
    conditionGroup: ConditionGroupNode,
    action: ActionNode,
    addButton: AddButton,
}

const normalizeArray = (value) => Array.isArray(value) ? [...value] : []

const sortStrings = (value) => normalizeArray(value).map(String).sort()

const safeParseActionParams = (value) => {
    if (!value) return {}
    if (typeof value === 'object') return value

    try {
        return JSON.parse(value.replace(/'/g, '"'))
    } catch {
        return {}
    }
}

const normalizeForCompare = (value) => {
    if (Array.isArray(value)) {
        return value.map((item) => normalizeForCompare(item))
    }

    if (value && typeof value === 'object') {
        return Object.keys(value)
            .sort()
            .reduce((acc, key) => {
                acc[key] = normalizeForCompare(value[key])
                return acc
            }, {})
    }

    return value
}

const areObjectsEqual = (left, right) => (
    JSON.stringify(normalizeForCompare(left)) === JSON.stringify(normalizeForCompare(right))
)

const normalizeCondition = (condition) => ({
    version: condition?.version || 'CURRENT',
    fieldName: condition?.fieldName || '',
    conditionType: condition?.conditionType || '',
    conditionValue: condition?.conditionValue || '',
})

const WorkflowBuilderInner = () => {
    const { id: automationId } = useParams()
    const isEditMode = !!automationId
    const navigate = useNavigate()

    const [selectedNodeId, setSelectedNodeId] = useState(null)
    const [activeView, setActiveView] = useState(() => (
        isEditMode && window.location.hash === '#history' ? 'history' : 'workflow'
    ))
    const [error, setError] = useState(null)
    const [saving, setSaving] = useState(false)
    const [pendingConditionDeletes, setPendingConditionDeletes] = useState([])
    const [pendingConditionGroupDeletes, setPendingConditionGroupDeletes] = useState([])

    useEffect(() => {
        const syncViewWithFragment = () => {
            const requestedView = window.location.hash.replace('#', '')
            const nextView = requestedView === 'history' && isEditMode ? 'history' : 'workflow'
            setActiveView(nextView)
            if (nextView === 'history') setSelectedNodeId(null)

            if (window.location.hash !== `#${nextView}`) {
                window.history.replaceState(
                    null,
                    '',
                    `${window.location.pathname}${window.location.search}#${nextView}`
                )
            }
        }

        syncViewWithFragment()
        window.addEventListener('hashchange', syncViewWithFragment)
        return () => window.removeEventListener('hashchange', syncViewWithFragment)
    }, [isEditMode])

    const { data: modelsData } = useQuery(AllAvailableModelsQuery)
    const { data: actionTypesData } = useQuery(ActionTypesAndParamsQuery)
    const { data: condTypesData } = useQuery(ConditionTypes)
    const { data: trigTypesData } = useQuery(TriggerTypes)

    const { data: automationData, refetch: refetchAutomation } = useQuery(AutomationQuery, {
        variables: { id: automationId },
        skip: !isEditMode,
        fetchPolicy: 'network-only',
    })

    const allAvailableModels = modelsData?.allAvailableModels
    const actionTypesAndParams = actionTypesData?.actionTypesAndParams
    const conditionTypes = condTypesData?.conditionTypes
    const triggerTypes = trigTypesData?.triggerTypes

    const wf = useWorkflowState(null)

    useEffect(() => {
        if (automationData?.automation && !wf.initialized) {
            wf.loadFromAutomation(automationData.automation)
        }
    }, [automationData, wf])

    const startNode = wf.nodes.find((n) => n.id === 'start')
    const contentTypeId = startNode?.data?.availableModel?.contentType?.id
        || startNode?.data?.contentTypeId
        || null

    const { data: avModelData } = useQuery(AvailableModelQuery, {
        variables: { contentTypeId },
        skip: !contentTypeId,
    })
    const availableModel = startNode?.data?.availableModel || avModelData?.availableModel || null
    const workflowName = automationData?.automation?.name || startNode?.data?.name || (isEditMode ? 'Automation' : 'Untitled Automation')
    const workflowIsActive = startNode?.data?.isActive !== false

    useEffect(() => {
        if (avModelData?.availableModel && isEditMode && startNode && !startNode.data.availableModel) {
            wf.updateNodeData('start', { availableModel: avModelData.availableModel })
        }
    }, [avModelData, isEditMode, startNode, wf])

    const [createWorkflow] = useMutation(CREATE_AUTOMATION_WORKFLOW)
    const [updateAutomation] = useMutation(UpdateAutomationMutation)
    const [updateTrigger] = useMutation(UpdateTriggerMutation)
    const [createAction] = useMutation(CreateActionMutation)
    const [updateAction] = useMutation(UpdateActionMutation)
    const [deleteAction] = useMutation(DeleteActionMutation)
    const [createConditionGroup] = useMutation(CreateConditionGroupMutation)
    const [updateConditionGroup] = useMutation(UpdateConditionGroupMutation)
    const [createCondition] = useMutation(CreateConditionMutation)
    const [updateCondition] = useMutation(UpdateConditionMutation)
    const [deleteCondition] = useMutation(DeleteConditionMutation)
    const [deleteConditionGroup] = useMutation(DeleteConditionGroupMutation)

    const originalAutomationState = useMemo(() => {
        const automation = automationData?.automation
        if (!automation) return null

        const actionsById = new Map(
            (automation.actionSet?.edges || []).map(({ node }) => [
                node.id,
                {
                    actionType: node.actionType || '',
                    actionParams: safeParseActionParams(node.actionParams),
                },
            ])
        )

        const conditionGroupsById = new Map(
            (automation.conditiongroupSet?.edges || []).map(({ node }) => [
                node.id,
                {
                    logicalOperator: node.logicalOperator || 'AND',
                    conditionsById: new Map(
                        (node.conditionSet?.edges || []).map(({ node: condition }) => [
                            condition.id,
                            normalizeCondition(condition),
                        ])
                    ),
                },
            ])
        )

        return {
            automation: {
                name: automation.name || '',
                isActive: automation.isActive !== false,
            },
            trigger: automation.trigger ? {
                id: automation.trigger.id,
                triggerType: automation.trigger.triggerType || '',
                contentTypeId: automation.trigger.contentType?.id || null,
                columnsUpdated: sortStrings(automation.trigger.columnsUpdated),
            } : null,
            actionsById,
            conditionGroupsById,
        }
    }, [automationData])

    const selectedNode = useMemo(
        () => wf.nodes.find((n) => n.id === selectedNodeId) || null,
        [wf.nodes, selectedNodeId]
    )

    const onNodeClick = useCallback((event, node) => {
        if (node.type === 'addButton') {
            if (node.data.addType === 'conditionGroup') {
                wf.addConditionGroup()
            } else if (node.data.addType === 'action') {
                wf.addAction()
            }
            return
        }
        setSelectedNodeId(node.id)
    }, [wf])

    const handleUpdateNode = useCallback((nodeId, data) => {
        wf.updateNodeData(nodeId, data)
    }, [wf])

    const handleDeleteCondition = useCallback((nodeId, conditionId) => {
        if (!isEditMode || !conditionId) return

        setPendingConditionDeletes((prev) => (
            prev.includes(conditionId) ? prev : [...prev, conditionId]
        ))
    }, [isEditMode])

    const handleDeleteNode = useCallback((nodeId) => {
        const node = wf.nodes.find((n) => n.id === nodeId)
        if (!node) return

        if (node.type === 'conditionGroup') {
            wf.removeConditionGroup(nodeId)
        } else if (node.type === 'action') {
            wf.removeAction(nodeId)
        }
        setSelectedNodeId(null)

        if (isEditMode && node.data.dbId) {
            if (node.type === 'action') {
                deleteAction({ variables: { input: { id: node.data.dbId } } })
                    .then(() => refetchAutomation())
            } else if (node.type === 'conditionGroup') {
                const conditionIdsInGroup = (node.data.conditions || [])
                    .map((condition) => condition.id)
                    .filter((id) => id && !String(id).startsWith('temp_'))

                setPendingConditionGroupDeletes((prev) => (
                    prev.includes(node.data.dbId) ? prev : [...prev, node.data.dbId]
                ))
                setPendingConditionDeletes((prev) => (
                    prev.filter((id) => !conditionIdsInGroup.includes(id))
                ))
            }
        }
    }, [wf, isEditMode, deleteAction, refetchAutomation])

    const handleSaveCreate = useCallback(async () => {
        setError(null)
        setSaving(true)

        const input = wf.serializeForCreate()
        if (!input) {
            setError('Please configure the trigger and at least one action before saving.')
            setSaving(false)
            return
        }
        if (!input.name) {
            setError('Automation name is required.')
            setSaving(false)
            return
        }
        if (!input.availableModelId) {
            setError('Please select a model.')
            setSaving(false)
            return
        }
        if (!input.triggerType) {
            setError('Please select a trigger type.')
            setSaving(false)
            return
        }

        try {
            const { data } = await createWorkflow({ variables: { input } })
            if (data.createAutomationWorkflow.ok) {
                navigate(`${ROUTES.automations.path}${data.createAutomationWorkflow.automation.id}`)
            } else {
                setError(data.createAutomationWorkflow.errors?.[0]?.messages?.[0] || 'Failed to create workflow.')
            }
        } catch (e) {
            setError(e.message)
        } finally {
            setSaving(false)
        }
    }, [wf, createWorkflow, navigate])

    const handleSaveEdit = useCallback(async () => {
        setError(null)
        setSaving(true)

        try {
            const sNode = wf.nodes.find((n) => n.id === 'start')
            if (sNode && originalAutomationState) {
                const currentAutomation = {
                    name: sNode.data.name || '',
                    isActive: sNode.data.isActive !== false,
                }

                if (
                    currentAutomation.name !== originalAutomationState.automation.name
                    || currentAutomation.isActive !== originalAutomationState.automation.isActive
                ) {
                    await updateAutomation({
                        variables: {
                            input: {
                                id: automationId,
                                name: currentAutomation.name,
                                isActive: currentAutomation.isActive,
                            },
                        },
                    })
                }

                const currentTrigger = sNode.data.triggerId ? {
                    id: sNode.data.triggerId,
                    triggerType: sNode.data.triggerType || '',
                    contentTypeId: sNode.data.availableModel?.contentType?.id || sNode.data.contentTypeId || null,
                    columnsUpdated: sortStrings(sNode.data.columnsUpdated),
                } : null

                if (
                    currentTrigger
                    && originalAutomationState.trigger
                    && (
                        currentTrigger.triggerType !== originalAutomationState.trigger.triggerType
                        || currentTrigger.contentTypeId !== originalAutomationState.trigger.contentTypeId
                        || !areObjectsEqual(currentTrigger.columnsUpdated, originalAutomationState.trigger.columnsUpdated)
                    )
                ) {
                    await updateTrigger({
                        variables: {
                            input: {
                                id: currentTrigger.id,
                                triggerType: currentTrigger.triggerType,
                                contentTypeId: currentTrigger.contentTypeId,
                                columnsUpdated: currentTrigger.columnsUpdated,
                            },
                        },
                    })
                }
            }

            const actionNodes = wf.nodes.filter((n) => n.type === 'action')
            for (const actNode of actionNodes) {
                if (actNode.data.dbId) {
                    const originalAction = originalAutomationState?.actionsById.get(actNode.data.dbId)
                    const currentAction = {
                        actionType: actNode.data.actionType || '',
                        actionParams: actNode.data.actionParams || {},
                    }

                    if (
                        !originalAction
                        || currentAction.actionType !== originalAction.actionType
                        || !areObjectsEqual(currentAction.actionParams, originalAction.actionParams)
                    ) {
                        await updateAction({
                            variables: {
                                input: {
                                    id: actNode.data.dbId,
                                    actionType: currentAction.actionType,
                                    actionParams: JSON.stringify(currentAction.actionParams),
                                },
                            },
                        })
                    }
                } else if (actNode.data.actionType) {
                    await createAction({
                        variables: {
                            input: {
                                automationId,
                                actionType: actNode.data.actionType,
                                actionParams: JSON.stringify(actNode.data.actionParams || {}),
                            },
                        },
                    })
                }
            }

            const cgNodes = wf.nodes.filter((n) => n.type === 'conditionGroup')
            for (const cgNode of cgNodes) {
                let groupId = cgNode.data.dbId
                if (!groupId) {
                    const { data: cgData } = await createConditionGroup({
                        variables: {
                            input: {
                                automationId,
                                logicalOperator: cgNode.data.logicalOperator,
                            },
                        },
                    })
                    if (cgData?.createConditionGroup?.ok) {
                        groupId = cgData.createConditionGroup.conditionGroup?.id
                    }
                } else {
                    const originalGroup = originalAutomationState?.conditionGroupsById.get(groupId)
                    if (
                        !originalGroup
                        || (cgNode.data.logicalOperator || 'AND') !== originalGroup.logicalOperator
                    ) {
                        await updateConditionGroup({
                            variables: {
                                input: {
                                    id: groupId,
                                    logicalOperator: cgNode.data.logicalOperator || 'AND',
                                },
                            },
                        })
                    }
                }

                if (groupId) {
                    for (const cond of cgNode.data.conditions || []) {
                        if (cond.id && !String(cond.id).startsWith('temp_')) {
                            const originalCondition = originalAutomationState
                                ?.conditionGroupsById
                                .get(groupId)
                                ?.conditionsById
                                .get(cond.id)
                            const currentCondition = normalizeCondition(cond)

                            if (
                                !originalCondition
                                || !areObjectsEqual(currentCondition, originalCondition)
                            ) {
                                await updateCondition({
                                    variables: {
                                        input: {
                                            id: cond.id,
                                            fieldName: currentCondition.fieldName,
                                            conditionType: currentCondition.conditionType,
                                            conditionValue: currentCondition.conditionValue,
                                        },
                                    },
                                })
                            }
                        } else if (cond.fieldName && cond.conditionType) {
                            await createCondition({
                                variables: {
                                    input: {
                                        conditionGroupId: groupId,
                                        fieldName: cond.fieldName,
                                        conditionType: cond.conditionType,
                                        conditionValue: cond.conditionValue || '',
                                    },
                                },
                            })
                        }
                    }
                }
            }

            for (const conditionGroupId of pendingConditionGroupDeletes) {
                const { data } = await deleteConditionGroup({
                    variables: { input: { id: conditionGroupId } },
                })

                if (!data?.deleteConditionGroup?.ok) {
                    throw new Error(
                        data?.deleteConditionGroup?.errors?.[0]?.messages?.[0]
                        || 'Failed to delete condition group.'
                    )
                }
            }

            for (const conditionId of pendingConditionDeletes) {
                const { data } = await deleteCondition({
                    variables: { input: { id: conditionId } },
                })

                if (!data?.deleteCondition?.ok) {
                    throw new Error(
                        data?.deleteCondition?.errors?.[0]?.messages?.[0]
                        || 'Failed to delete condition.'
                    )
                }
            }

            await refetchAutomation()
            setPendingConditionDeletes([])
            setPendingConditionGroupDeletes([])
            setError(null)
        } catch (e) {
            setError(e.message)
        } finally {
            setSaving(false)
        }
    }, [
        wf, automationId, updateAutomation, updateTrigger,
        createAction, updateAction, createConditionGroup,
        updateConditionGroup,
        createCondition, updateCondition, deleteCondition,
        deleteConditionGroup, pendingConditionDeletes,
        pendingConditionGroupDeletes, refetchAutomation,
        originalAutomationState,
    ])

    const handleSave = isEditMode ? handleSaveEdit : handleSaveCreate

    const changeView = (view) => {
        setActiveView(view)
        if (view === 'history') setSelectedNodeId(null)
        if (window.location.hash !== `#${view}`) {
            window.location.hash = view
        }
    }

    return (
        <div className="wf-builder-container">
            <header className="wf-toolbar">
                <div className="wf-toolbar-main">
                    <div className="wf-toolbar-left">
                        <Link to={ROUTES.automations.path} className="wf-back-button" aria-label="Back to automations">
                            <i className="fa fa-arrow-left" />
                        </Link>
                        <div className="wf-workflow-mark"><i className="fa fa-code-fork" /></div>
                        <div className="wf-toolbar-identity">
                            <span>{isEditMode ? 'Automation' : 'Automation builder'}</span>
                            <div className="wf-toolbar-title-row">
                                <h1 title={toTitleCase(workflowName)}>{toTitleCase(workflowName)}</h1>
                                {isEditMode && (
                                    <span className={`wf-toolbar-status ${workflowIsActive ? 'active' : 'inactive'}`}>
                                        <i></i>{workflowIsActive ? 'Active' : 'Inactive'}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="wf-toolbar-right">
                        {activeView === 'history' ? (
                            <button type="button" className="wf-edit-workflow-button" onClick={() => changeView('workflow')}>
                                <i className="fa fa-pencil" /> Edit workflow
                            </button>
                        ) : (
                            <Button
                                color="primary"
                                size="sm"
                                className="wf-save-button"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                <i className={`fa ${saving ? 'fa-circle-o-notch fa-spin' : 'fa-check'}`} />
                                {saving ? 'Saving...' : 'Save workflow'}
                            </Button>
                        )}
                    </div>
                </div>
                <nav className="wf-view-tabs" aria-label="Automation views">
                    <button
                        type="button"
                        className={activeView === 'workflow' ? 'active' : ''}
                        aria-current={activeView === 'workflow' ? 'page' : undefined}
                        onClick={() => changeView('workflow')}
                    >
                        <i className="fa fa-code-fork" /> Workflow
                    </button>
                    {isEditMode && (
                        <button
                            type="button"
                            className={activeView === 'history' ? 'active' : ''}
                            aria-current={activeView === 'history' ? 'page' : undefined}
                            onClick={() => changeView('history')}
                        >
                            <i className="fa fa-history" /> History
                        </button>
                    )}
                </nav>
            </header>

            {error && (
                <Alert color="danger" className="wf-alert" toggle={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {activeView === 'workflow' ? (
                <div className="wf-canvas-wrapper">
                    <div className={`wf-canvas ${selectedNode ? 'wf-canvas-with-panel' : ''}`}>
                        <ReactFlow
                            nodes={wf.nodes}
                            edges={wf.edges}
                            nodeTypes={nodeTypes}
                            onNodeClick={onNodeClick}
                            onPaneClick={() => setSelectedNodeId(null)}
                            fitView
                            fitViewOptions={{ padding: 0.45 }}
                            nodesDraggable={false}
                            nodesConnectable={false}
                            edgesFocusable={false}
                            deleteKeyCode={null}
                            proOptions={{ hideAttribution: true }}
                        >
                            <Background variant="dots" gap={22} size={1.2} color="#d7dce5" />
                            <Controls showInteractive={false} />
                            <MiniMap
                                nodeColor={(n) => {
                                    if (n.type === 'start') return '#1769e0'
                                    if (n.type === 'conditionGroup') return '#f0a020'
                                    if (n.type === 'action') return '#12a474'
                                    return '#d8dee8'
                                }}
                                maskColor="rgba(24,34,48,0.06)"
                            />
                        </ReactFlow>
                    </div>

                    <ConfigPanel
                        selectedNode={selectedNode}
                        onClose={() => setSelectedNodeId(null)}
                        onUpdateNode={handleUpdateNode}
                        onDeleteNode={handleDeleteNode}
                        onDeleteCondition={handleDeleteCondition}
                        allAvailableModels={allAvailableModels}
                        triggerTypes={triggerTypes}
                        conditionTypes={conditionTypes}
                        actionTypesAndParams={actionTypesAndParams}
                        availableModel={availableModel}
                    />
                </div>
            ) : (
                <ExecutionLogsList automationId={automationId} />
            )}
        </div>
    )
}

const WorkflowBuilder = () => (
    <ReactFlowProvider>
        <WorkflowBuilderInner />
    </ReactFlowProvider>
)

export default WorkflowBuilder
